import { Input, Popover, Select, Tag, Tooltip, notification } from 'antd';
import debounce from 'lodash.debounce';
import PropTypes from 'prop-types';
import qs from 'query-string';
import React, { useEffect, useRef, useState } from 'react';
import { injectIntl } from 'react-intl';
import styled from 'styled-components';
import { withRouter } from 'utils/withRouter';
import { Flex, Icon } from '../../ui';
import { urlSigns } from '../../utils/app-configs';
import { transformUrlParamsToSearchFilters } from '../../utils/helpers';
import SearchFilters from './search-filters';

const { Search } = Input;

const Container = styled(Flex)`
  width: 100%;
  justify-content: flex-start;
  position: relative;
`;

const StyledSelect = styled(Select)`
  width: 12%;
  position: absolute;
  transition: all .5s;
  left: ${p => p.hasFocus ? '3%' : '15%'};
  font-weight: 400;
  font-size: 14px;
  height: 32px !important;
  .ant-select-selector {
    height: 32px !important;
    border-top-left-radius: 6px !important;
    border-bottom-left-radius: 6px !important;
    border-top-right-radius: 0px !important;
    border-bottom-right-radius: 0px !important;
    height: 31px !important;
    background-color: rgb(187,225,238) !important;
    border-color: #d9d9d9 !important;
    box-shadow: none !important;
  }
`;

const StyledSearch = styled(Search)`
  width: ${p => p.hasFocus ? '80%' : '70%'};
  height: 31px !important;
  transition: all .5s;
  margin-left: 15%;
  z-index: 1;
  .ant-input-group-addon {
    display: none;
  }
  .ant-input-prefix {
    height: 16px;
    i {
      color: ${p => p.hasFocus ? '#313b40' : p.theme.colors.theme};
    }
    cursor: ${p => p.hasFocus ? 'pointer' : 'default'}
  }
  .ant-input-affix-wrapper {
    height: 31px !important;
    align-items: center;
    border-radius: 6px !important;
    ${p => p.hasFocus && `
      border-top-left-radius: 0px !important;
      border-bottom-left-radius: 0px !important;
    `}
    background-color: ${p => p.hasFocus ? 'white' : 'rgb(187,225,238)'};
    &:hover {
      border-color: #d9d9d9 !important;
      box-shadow: none !important;
    }
  }
  .ant-input-affix-wrapper-focused {
    border-color: #d9d9d9 !important;
    box-shadow: none !important;
  }
  #search_input {
    font-size: 16px;
    height: 24px;
    transition: none !important;
    background-color: inherit;
    width: calc(100% - ${p => p.tagsWidth || 0}px) !important;
  }
  .ant-input-suffix {
    margin-left: auto !important;
  }
  .ant-input-suffix > *:not(:last-child) {
    margin-right: -11px !important;
  }
  #search_input::placeholder {
    color: ${p => p.hasFocus ? '#BEBFC5' : p.theme.colors.theme};
    opacity: 1;
  }
  .ant-popover-placement-bottomRight {
    padding-top: 0px !important;
    width: 100%;
  }
`;

const StyledButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background-color: rgba(49, 59, 64, 0.1);
  height: 29px;
  width: 44px;
  margin: -6px -11px -6px 0px;
  border-top-right-radius: 6px;
  border-bottom-right-radius: 6px;
  i {
    color: #313b40;
    cursor: pointer;
  }
`;

const StyledTags = styled.div`
  display: flex;
  align-items: center;
  width: auto;
  .ant-tag {
    font-size: 14px !important;
  }
  position: absolute;
  ${p => !p.maxWidth && `
    right: auto !important;
    left: calc(18% + ${p.left * 1.04}px) !important;
  `}

  ${p => p.maxWidth && `
    right: 8% !important;
    left: auto !important;
  `}
  z-index: 2;
  background-color: transparent !important;
  height: 31px;
`;

const InputText = styled.p`
  width: auto;
  visibility: hidden;
  height: 0;
  position: absolute;
  top: 0;
  left: 0;
`;

const StyledTagDescription = styled.span`
  margin-right: 10px;
`;

const StyledTagIcon = styled(Icon)`
  float: right;
  color: white;
  line-height: 25px;
`;

const SearchBar = ({
  searchValue,
  clearInput,
  history,
  getSearchFilters,
  searchFilters,
  isLoadingFilters,
  intl,
  shouldRefetch,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [searchType, setSearchType] = useState('all');
  const [hasFocus, setHasFocus] = useState(false);
  const [isPopoverVisible, setIsPopoverVisible] = useState(false);
  const [overflowTags, setOverflowTags] = useState(false);
  const [tagsWidth, setTagsWidth] = useState(0);
  const [tagOffset, setTagOffset] = useState(0);
  const [tags, setTags] = useState([]);
  const [softClear, setSoftClear] = useState(false);
  const inputRef = useRef(null);
  const textRef = useRef(null);
  const tagsRef = useRef(null);

  useEffect(() => {
    getSearchFilters();
  }, []);

  useEffect(() => {
    const textWidth = textRef?.current?.clientWidth;
    if (textWidth) {
      if (textWidth >= inputRef?.current?.input?.clientWidth - 75) {
        setOverflowTags(true);
      } else {
        setOverflowTags(false);
      }
    }
  }, [inputValue, textRef?.current?.clientWidth]);

  useEffect(() => {
    setTagOffset(textRef?.current?.clientWidth || 0);
  }, [inputValue, searchValue, textRef?.current?.clientWidth]);

  const clearSearch = (hasTags) => {
    if (searchValue) {
      clearInput();
    }
    setSearchType('all');
    if (!hasTags) {
      if (history.location.pathname.includes('/search')) {
        history.push('/home');
      }
      setInputValue('');
      if (softClear) {
        setHasFocus(true);
        inputRef?.current?.focus();
        setSoftClear(false);
      } else {
        setHasFocus(false);
        inputRef?.current?.blur();
      }
    }
  };

  const performSearch = (v, type, location, hasTags) => {
    if ((v && v.trim()) || hasTags) {
      if (v === '\\') {
        notification.error({
          message: intl.formatMessage({ id: 'search.SEARCH_ERROR' }),
          key: 'specialCharacterError',
          placement: 'top',
        });
        clearSearch(false);
      } else if (type === 'all') {
        history.replace(
          `/search/${encodeURIComponent(v || '*')}${location.search || ''}`,
          location.state,
        );
      } else {
        history.replace(
          `/search/${encodeURIComponent(v || '*')}/type/${type}${location.search || ''}`,
          location.state,
        );
      }
    } else if (!hasTags) {
      setSoftClear(true);
      history.push('/home');
    }
  };

  useEffect(() => {
    setTagsWidth(tagsRef?.current?.clientWidth || 0);
  }, [tags, tagsRef?.current?.clientWidth]);

  useEffect(() => {
    const params = history.location.pathname.split('/type/');
    const type = params[1]?.replace(/\/.*/g, '');
    if ((type || 'all') !== searchType) {
      setSearchType(type || 'all');
    }
    if (!history.location.pathname.includes('/search')) {
      clearSearch(tags?.length);
    }
  }, [history.location.pathname]);

  useEffect(() => {
    const params = qs.parse(history.location.search);
    if (
      params.searchFilters &&
      searchType &&
      searchFilters[searchType]
    ) {
      const filterArray = searchFilters[searchType]?.filters;
      const currentFilters = transformUrlParamsToSearchFilters(
        decodeURIComponent(params.searchFilters),
      );
      let queryString = '';
      if (currentFilters && Object.keys(currentFilters).length) {
        queryString = Object.entries(currentFilters).reduce((tot, [key, value]) => {
          if (filterArray?.find(f => f.id === key)) {
            const filterString = `${key}${urlSigns.keyValueSeparator}${value}`;
            return tot ? `${tot}${urlSigns.filterSeparator}${filterString}` : filterString;
          }
          return tot;
        }, '');
      }
      if (inputValue === '\\') {
        notification.error({
          message: intl.formatMessage({ id: 'search.SEARCH_ERROR' }),
          key: 'specialCharacterError',
          placement: 'top',
        });
        clearSearch(false);
      } else {
        history.replace(`/search/${encodeURIComponent(inputValue || '*')}${searchType === 'all' ?
          '' :
          `/type/${searchType}`
          }`,
          {
            searchFilters: searchFilters[searchType].hasInheritance ? queryString : '',
          });
      }
    } else if (
      inputValue &&
      !params.searchFilters &&
      searchType &&
      searchFilters[searchType] &&
      history.location.pathname.includes('/search')
    ) {
      if (inputValue === '\\') {
        notification.error({
          message: intl.formatMessage({ id: 'search.SEARCH_ERROR' }),
          key: 'specialCharacterError',
          placement: 'top',
        });
        clearSearch(false);
      } else {
        history.replace(`/search/${encodeURIComponent(inputValue)}${searchType === 'all' ?
          '' :
          `/type/${searchType}`
          }`,
          {
            searchFilters: '',
          },
        );
      }
    }
  }, [searchType]);

  useEffect(() => {
    const params = qs.parse(history.location.search);
    if (params.searchFilters) {
      const filters = decodeURIComponent(params.searchFilters)
        .split(urlSigns.filterSeparator);
      setTags(filters?.filter(f => f)?.map((f) => {
        const [id, description] = f.split(urlSigns.keyValueSeparator);
        return {
          id,
          description,
        };
      }));
    } else {
      setTags([]);
    }
  }, [history.location.search]);

  useEffect(() => {
    setInputValue(searchValue);
  }, [searchValue]);

  const handleSelect = (v) => {
    setSearchType(v);
  };

  const debouncedSearch = useRef(
    debounce((v, type, search, hasTags) => performSearch(v, type, search, hasTags), 600),
  ).current;

  const onSearchvalueChanged = (event) => {
    setInputValue(event.target.value);
    debouncedSearch(event.target.value, searchType, history.location, tags?.length > 0);
  };

  const handleDropdownClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const getPlaceHolder = () => {
    if (hasFocus) {
      if (!inputValue && tags?.length) {
        return null;
      }
      // eslint-disable-next-line max-len
      return intl.formatMessage({ id: 'search.PLACEHOLDER_FOCUS' });
    }
    return intl.formatMessage({ id: 'search.SEARCH' });
  };

  const handleVisibleChange = (o) => {
    setIsPopoverVisible(o);
  };

  const handleCloseTag = (id) => {
    const params = qs.parse(history.location.search);
    if (params.searchFilters) {
      const filters = params.searchFilters.split(urlSigns.filterSeparator);
      const newFilters = filters?.map((f) => {
        const [fId, value] = f.split(urlSigns.keyValueSeparator);
        return {
          id: fId,
          value,
        };
      })?.filter(f => f.id !== id);
      const queryString = newFilters
        ?.map(f => `${f.id}${urlSigns.keyValueSeparator}${f.value}`)
        .join(urlSigns.filterSeparator);
      if (!inputValue && !queryString) {
        clearSearch(false);
      } else if (inputValue === '\\') {
        notification.error({
          message: intl.formatMessage({ id: 'search.SEARCH_ERROR' }),
          key: 'specialCharacterError',
          placement: 'top',
        });
        clearSearch(false);
      } else {
        history.replace(history.location.pathname, {
          searchFilters: queryString ? encodeURIComponent(queryString) : '',
        });
      }
      shouldRefetch(true);
    }
  };

  const renderTags = () => {
    const tagLimit = 4;
    if (tags?.length > tagLimit) {
      return (
        <StyledTags
          ref={tagsRef}
          left={tagOffset}
          maxWidth={overflowTags}
        >
          {tags?.map((t, i) => {
            if (i < tagLimit) {
              const tagDescription = t.description;
              const isLongTag = tagDescription?.length > 20;
              const tagElem =
                <Tag
                  key={t.id}
                  color='blue'
                  closable={true}
                  onClose={() => handleCloseTag(t.id)}
                >
                  {isLongTag ? `${tagDescription?.slice(0, 20)}...` : tagDescription}
                </Tag>;
              return isLongTag ?
                <Tooltip
                  title={tagDescription}
                  key={t.id}
                  overlayStyle={{ maxWidth: window.innerWidth || '500px' }}
                >
                  {tagElem}
                </Tooltip>
                :
                tagElem;
            }
            return null;
          })}
          <Tooltip
            title={tags?.filter((t, index) => index >= tagLimit)?.map(t =>
              <span key={t.id}>
                <StyledTagDescription>{t.description}</StyledTagDescription>
                <StyledTagIcon type='times' onClick={() => handleCloseTag(t.id)} />
              </span>,
            )}
            overlayStyle={{ maxWidth: window.innerWidth || '500px' }}
            overlayInnerStyle={{
              display: 'flex',
              flexFlow: 'column nowrap',
              fontSize: '14px',
            }}
          >
            <Tag
              color='blue'
              closable={false}
            >
              <Icon type='ellipsis-h' />
            </Tag>
          </Tooltip>
        </StyledTags>
      );
    }
    return (
      <StyledTags
        ref={tagsRef}
        left={tagOffset}
        maxWidth={overflowTags}
      >
        {tags?.map((t) => {
          const tagDescription = t.description;
          const isLongTag = tagDescription?.length > 20;
          const tagElem =
            <Tag
              key={t.id}
              color='blue'
              closable={true}
              onClose={() => handleCloseTag(t.id)}
            >
              {isLongTag ? `${tagDescription?.slice(0, 20)}...` : tagDescription}
            </Tag>;
          return isLongTag ?
            <Tooltip
              title={tagDescription}
              key={t.id}
              overlayStyle={{ maxWidth: window.innerWidth || '500px' }}
            >
              {tagElem}
            </Tooltip>
            :
            tagElem;
        })}
      </StyledTags>
    );
  };

  return (
    <Container
      onFocus={() => setHasFocus(true)}
      onBlur={() => (!isPopoverVisible && !inputValue && !tags?.length) && setHasFocus(false)}
    >
      {<StyledSelect
        options={searchFilters ?
          Object.keys(searchFilters)
            ?.filter(f => searchFilters[f]?.showInSearchBar)
            ?.map(o => ({
              value: o,
              label: searchFilters[o]?.description,
            })) :
          []
        }
        hasFocus={hasFocus || inputValue}
        value={searchType}
        onSelect={handleSelect}
        disabled={isLoadingFilters}
      />}
      <StyledSearch
        data-test-id='search-box'
        ref={inputRef}
        placeholder={getPlaceHolder()}
        value={inputValue}
        onChange={onSearchvalueChanged}
        prefix={<Icon
          type={hasFocus || inputValue ? 'times' : 'search'}
          fontSize={16}
          onClick={() => (hasFocus || inputValue) && clearSearch(false)}
        />}
        id='search_input'
        hasFocus={hasFocus || inputValue}
        readOnly={isPopoverVisible}
        enterButton={false}
        tagsWidth={tagsWidth + 75}
        autoComplete='off'
        suffix={(hasFocus || inputValue) &&
          !isLoadingFilters &&
          searchFilters[searchType]?.filters?.length &&
          <Popover
            overlayClassName='search-filters'
            content={<SearchFilters
              filters={searchFilters[searchType]?.filters}
              searchValue={inputValue}
              closeFilters={() => handleVisibleChange(false)}
              searchType={searchType}
              refetch={() => shouldRefetch(true)}
              clear={() => clearSearch(false)}
            />}
            // The popup container should be the input and not the dropdownbutton
            getPopupContainer={triggerNode => triggerNode.parentNode.parentNode.parentNode}
            placement='bottomRight'
            trigger='click'
            open={isPopoverVisible}
            onVisibleChange={handleVisibleChange}
            destroyTooltipOnHide={true}
          >
            <StyledButton
              onMouseDown={handleDropdownClick}
            >
              <Icon
                type='chevron-down'
                fontSize={16}
              />
            </StyledButton>
          </Popover>
        }
      />
      {/* Hidden element to calculate the width of the input text */}
      <InputText ref={textRef}>{inputValue}</InputText>
      {(hasFocus || inputValue) &&
        tags?.length ?
        renderTags() :
        null
      }
    </Container >
  );
};

SearchBar.propTypes = {
  history: PropTypes.object.isRequired,
  searchValue: PropTypes.string.isRequired,
  clearInput: PropTypes.func,
  getSearchFilters: PropTypes.func,
  searchFilters: PropTypes.object,
  isLoadingFilters: PropTypes.bool,
  intl: PropTypes.object,
  shouldRefetch: PropTypes.func,
};

export default withRouter(injectIntl(SearchBar));
